"use client";

import { Dot, Instagram, Share2 } from "lucide-react";
import Image from "next/image";
import Link from "next/link";
import { useRouter } from "next/navigation";
import { useCallback } from "react";

import { services } from "@/data/services";
import { share } from "@/utils/share";

import { Button } from "../ui/button";

const links = [
  { label: "Início", href: "/" },
  { label: "Sobre nós", href: "/#sobre" },
  { label: "Depoimentos", href: "/#depoimentos" },
  { label: "Perguntas Frequentes", href: "/#faq" },
  { label: "Blog", href: "/blog" },
  { label: "Contato", href: "/#contato" },
];

export function Footer() {
  const router = useRouter();

  const handleClick = useCallback(
    (e: React.MouseEvent<HTMLAnchorElement>, href: string) => {
      if (href.startsWith("/#")) {
        e.preventDefault();

        const targetId = href.split("#")[1];
        const targetElement = document.getElementById(targetId);

        if (targetElement) {
          targetElement.scrollIntoView({ behavior: "smooth", block: "start" });
        } else {
          router.push("/#" + targetId);
        }
      } else {
        router.push(href);
      }
    },
    [router],
  );

  return (
    <footer className="relative mt-12 overflow-hidden bg-[#1C4153] text-white md:m-4 md:rounded-lg">
      <div className="mx-auto max-w-7xl px-6 pt-14 pb-8">
        <div className="grid grid-cols-1 gap-12 sm:grid-cols-2 lg:grid-cols-4">
          <div className="flex flex-col items-center gap-2 md:items-start">
            <Image
              src="/logo-v2.png"
              alt="São Chico Clínica Veterinária"
              width={200}
              height={150}
              className={`h-auto w-50`}
            />

            <div className="mt-4 flex items-center gap-3">
              <Link
                href={process.env.NEXT_PUBLIC_INSTAGRAM_URL || "#"}
                target="_blank"
                rel="noopener noreferrer"
                className="flex h-9 w-9 items-center justify-center rounded-lg bg-white/10 transition-colors hover:bg-white/20"
                aria-label="Instagram"
              >
                <Instagram className="h-5 w-5" strokeWidth={1.5} />
              </Link>

              <Button
                onClick={() =>
                  share({
                    title: "São Chico - Clínica Veterinária",
                    text: "Confira a São Chico, clínica veterinária em Lages!",
                    url:
                      process.env.NEXT_PUBLIC_URL_SITE ||
                      "https://clinicasaochico.com.br",
                  })
                }
                aria-label="Share website"
                className="h-9 w-9 rounded-lg bg-white/10 transition-colors hover:bg-white/20"
              >
                <Share2 className="h-5 w-5" strokeWidth={1.5} />
              </Button>
            </div>
          </div>

          <div>
            <p className="text-lg font-semibold tracking-wide text-white">
              Links
            </p>
            <div className="bg-secondary mt-1 h-[2px] w-10"></div>

            <ul className="mt-6 space-y-3">
              {links.map((link) => (
                <li key={link.label}>
                  <Link
                    href={link.href}
                    onClick={(e) => handleClick(e, link.href)}
                    className="hover:text-secondary flex items-center gap-1 text-sm transition-all"
                  >
                    <Dot className="text-secondary h-4 w-4" /> {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <p className="text-lg font-semibold tracking-wide text-white">
              Nossos serviços
            </p>
            <div className="bg-secondary mt-1 h-[2px] w-10"></div>

            <ul className="mt-6 space-y-3">
              {services.map((service) => (
                <li key={service.id}>
                  <Link
                    href="/#servicos"
                    onClick={(e) => handleClick(e, "/#servicos")}
                    className="hover:text-secondary flex items-center gap-1 text-sm transition-all"
                  >
                    <Dot className="text-secondary h-4 w-4" /> {service.title}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <p className="text-lg font-semibold tracking-wide text-white">
              Contato
            </p>
            <div className="bg-secondary mt-1 h-[2px] w-10"></div>

            <div className="pt-4">
              <Link
                className="mt-6 pt-2 text-sm"
                href={`tel:${process.env.NEXT_PUBLIC_WHATSAPP_NUMBER}`}
              >
                {process.env.NEXT_PUBLIC_WHATSAPP_NUMBER}
              </Link>
            </div>

            <p className="mt-8 text-lg font-semibold tracking-wide text-white">
              Horário de Funcionamento
            </p>
            <div className="bg-secondary mt-1 h-[2px] w-10"></div>

            <div className="text-secondary mt-6 space-y-1 text-sm font-semibold">
              Atendimento 24 horas
            </div>
          </div>
        </div>

        <div className="mt-10 border-t border-white/10 pt-6">
          <div className="flex flex-col items-center justify-between gap-4 text-sm text-gray-200 md:flex-row">
            <p className="text-center md:text-start">
              © {new Date().getFullYear()} São Chico - Clínica Veterinária e
              Petshop. Todos os direitos reservados.
            </p>

            <span className="flex items-center gap-1">
              Desenvolvido por{" "}
              <Link
                href="https://www.agenciavolari.com.br"
                target="_blank"
                className="font-extrabold"
              >
                Agência Volari
              </Link>
            </span>
          </div>
        </div>
      </div>
    </footer>
  );
}
